/**
 * Logs Module
 * 
 * Handles log viewing with filtering and auto-refresh.
 */

import { api, LogEntry } from './api';

// State
let currentLevel = '';
let currentSearch = '';
let autoRefresh = false;
let refreshTimer: number | null = null;

const REFRESH_INTERVAL = 5_000;

/**
 * Initialize logs panel.
 */
export function initLogs(): void {
    // Level filter buttons
    const levelBtns = document.querySelectorAll('.level-btn');
    levelBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            levelBtns.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            currentLevel = (btn as HTMLElement).dataset.level || '';
            refreshLogs();
        });
    });

    // Search input
    const searchInput = document.getElementById('logs-search') as HTMLInputElement;
    if (searchInput) {
        let debounceTimer: number;
        searchInput.addEventListener('input', () => {
            clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
                currentSearch = searchInput.value.trim();
                refreshLogs();
            }, 300);
        });
    }

    // Auto-refresh toggle
    const autoRefreshToggle = document.getElementById('logs-auto-refresh') as HTMLInputElement;
    if (autoRefreshToggle) {
        autoRefreshToggle.addEventListener('change', () => {
            autoRefresh = autoRefreshToggle.checked;
            if (autoRefresh) {
                startAutoRefresh();
            } else {
                stopAutoRefresh();
            }
        });
    }

    // Initial load
    refreshLogs();
}

/**
 * Stop logs auto-refresh.
 */
export function stopLogs(): void {
    stopAutoRefresh();
}

/**
 * Refresh logs from API.
 */
export async function refreshLogs(): Promise<void> {
    try {
        const result = await api.getLogs({
            level: currentLevel || undefined,
            logger_name: currentSearch || undefined,
            limit: 100,
            include_persisted: true,
        });

        renderLogs(result.logs);
    } catch (err) {
        console.error('Failed to fetch logs:', err);
    }
}

function startAutoRefresh(): void {
    if (refreshTimer) return;
    refreshTimer = window.setInterval(refreshLogs, REFRESH_INTERVAL);
}

function stopAutoRefresh(): void {
    if (refreshTimer) {
        clearInterval(refreshTimer);
        refreshTimer = null;
    }
}

// ============================================================================
// Rendering
// ============================================================================

function renderLogs(logs: LogEntry[]): void {
    const container = document.getElementById('logs-container');
    if (!container) return;

    if (logs.length === 0) {
        container.innerHTML = `
      <div class="log-entry">
        <span class="log-message text-muted">No logs found</span>
      </div>
    `;
        return;
    }

    container.innerHTML = logs.map(log => `
    <div class="log-entry">
      <span class="log-time">${formatTime(log.timestamp)}</span>
      <span class="log-level ${log.level}">${log.level}</span>
      <span class="log-logger" title="${escapeHtml(log.logger)}">${truncate(log.logger, 30)}</span>
      <span class="log-message">${escapeHtml(log.message)}</span>
    </div>
  `).join('');

    // Scroll to bottom for newest
    container.scrollTop = container.scrollHeight;
}

// ============================================================================
// Helpers
// ============================================================================

function formatTime(timestamp: string): string {
    try {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', { hour12: false });
    } catch {
        return timestamp;
    }
}

function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function truncate(text: string, maxLen: number): string {
    return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
}
